import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Drawer } from "@/components/paisa/ui";
import { useNotifPrefs } from "@/components/landing/Header";
import TrustCenter from "@/components/settings/TrustCenter";
import { AntiPhishingPanel, DeleteAccountPanel, TwoFactorPanel } from "@/pages/app/Settings";
import { VerifyContactModal } from "@/pages/app/Profile";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";

const CURRENCY_KEY = "cm_display_currency";
const NOTIF_ROWS = [
  ["trades", "Trade and escrow updates"],
  ["price", "Price alerts"],
  ["security", "Security alerts"],
  ["marketing", "Product news"],
];
const input = "w-full max-w-xs rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm outline-none focus:border-primary";
const smallBtn = "shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs transition hover:border-primary disabled:opacity-50";

function Toggle({ on, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(on)}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`h-6 w-11 shrink-0 rounded-full p-0.5 transition disabled:opacity-50 ${on ? "brand-gradient" : "border border-border bg-surface-2"}`}
    >
      <span className={`block h-5 w-5 rounded-full bg-foreground transition-transform ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

function Section({ title, children, aside }) {
  return (
    <section className="rounded-xl border border-border bg-surface/50 p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {aside}
      </div>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

function Row({ label, hint, children, testid }) {
  return (
    <div className="flex items-center justify-between gap-4" data-testid={testid}>
      <div className="min-w-0">
        <p className="text-sm">{label}</p>
        {hint && <p className="truncate text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function SecurityScore({ checks }) {
  const done = checks.filter(Boolean).length;
  const score = Math.round((done / checks.length) * 100);
  return (
    <span className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="security-score">
      Score <span className={`num font-semibold ${score >= 80 ? "text-up" : score >= 50 ? "text-warning" : "text-down"}`}>{score}</span>/100
      <span className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-surface-2 sm:block">
        <span className="brand-gradient block h-full" style={{ width: `${score}%` }} />
      </span>
    </span>
  );
}

function PaymentMethods() {
  const [rows, setRows] = useState(null);
  const load = () =>
    api
      .get("/api/p2p/payment-methods")
      .then(({ data }) => setRows(Array.isArray(data) ? data : data?.items || data?.payment_methods || []))
      .catch(() => setRows([]));
  useEffect(() => {
    load();
  }, []);
  const remove = async (id) => {
    if (!window.confirm("Remove this payment method?")) return;
    try {
      await api.delete(`/api/p2p/payment-methods/${id}`);
    } finally {
      load();
    }
  };
  const mask = (n = "") => (n ? `····${String(n).slice(-4)}` : "");
  return (
    <Section title="Saved P2P payment methods">
      {rows === null ? (
        <div className="skeleton h-8 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No payment methods yet. Add one to post or take P2P offers.</p>
      ) : (
        rows.map((m) => (
          <Row
            key={m.id || m.payment_method_id}
            label={m.upi_id ? `UPI · ${m.upi_id}` : `${m.bank_name || "Bank"} ${mask(m.account_number)}`}
            hint={m.is_default ? "Default" : m.holder_name || m.type}
          >
            <button type="button" onClick={() => remove(m.id || m.payment_method_id)} className="shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs transition hover:border-down hover:text-down">
              Remove
            </button>
          </Row>
        ))
      )}
      <Link to="/p2p/payment-methods" className="inline-block rounded-lg border border-border px-3 py-2 text-xs transition hover:border-primary">
        Add payment method
      </Link>
    </Section>
  );
}

export default function ProfileSettings() {
  const { user, fetchMe } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { prefs, save: savePref } = useNotifPrefs(Boolean(user));
  const [drawer, setDrawer] = useState(null);
  const [verify, setVerify] = useState(null);
  const [phishCode, setPhishCode] = useState("");
  const [name, setName] = useState("");
  const [nameMsg, setNameMsg] = useState("");
  const [nameBusy, setNameBusy] = useState(false);
  const [safeBusy, setSafeBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [currency, setCurrency] = useState(() => {
    try {
      return localStorage.getItem(CURRENCY_KEY) || "USD";
    } catch {
      return "USD";
    }
  });

  useEffect(() => {
    if (!user) return;
    setName(user.full_name && user.full_name !== "-" ? user.full_name : "");
    api
      .get("/api/auth/anti-phishing-code")
      .then((r) => setPhishCode(r.data?.anti_phishing_code || ""))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const saveName = async () => {
    const clean = name.trim().replace(/\s+/g, " ");
    if (clean.length < 2) return setNameMsg("Enter your full name");
    setNameBusy(true);
    setNameMsg("");
    try {
      await api.patch("/api/auth/profile", { full_name: clean });
      await fetchMe();
      setNameMsg("Saved");
      setTimeout(() => setNameMsg(""), 2000);
    } catch (e) {
      setNameMsg(getErrorMessage(e));
    } finally {
      setNameBusy(false);
    }
  };

  const toggleSafeSession = async () => {
    setSafeBusy(true);
    try {
      await api.post("/api/auth/safe-session", { enabled: !user?.safe_session });
      await fetchMe();
    } finally {
      setSafeBusy(false);
    }
  };

  const changeCurrency = (v) => {
    setCurrency(v);
    try {
      localStorage.setItem(CURRENCY_KEY, v);
    } catch {
      /* storage unavailable */
    }
  };

  const accountId = user?.id ? `PCX-${String(user.id).slice(-8).toUpperCase()}` : "—";
  const nameChanged = name.trim() && name.trim() !== (user?.full_name || "");
  const pendingDeletion = user?.status === "pending_deletion";
  const closeDrawer = () => {
    setDrawer(null);
    fetchMe();
  };

  return (
    <AppShell>
      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-semibold">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">Changes to your email or mobile require a one-time code.</p>
        </div>

        <Section title="Profile">
          <Row label="Full name" hint={nameMsg || undefined}>
            <div className="flex w-full max-w-xs items-center gap-2">
              <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="As per your PAN" data-testid="settings-name" />
              {nameChanged && (
                <button type="button" onClick={saveName} disabled={nameBusy} className="brand-gradient shrink-0 rounded-lg px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-60">
                  Save
                </button>
              )}
            </div>
          </Row>
          <Row label="Account ID" hint="Share this with support when raising a ticket.">
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(accountId);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="num shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs transition hover:border-primary"
            >
              {copied ? "Copied" : accountId}
            </button>
          </Row>
          <Row label="Display currency">
            <select value={currency} onChange={(e) => changeCurrency(e.target.value)} className={input}>
              {["USD", "INR"].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Row>
          <Row label="Language">
            <select value="English" onChange={() => {}} className={input}>
              <option>English</option>
            </select>
          </Row>
          <Row label="Theme" hint="Dark is tuned for long trading sessions.">
            <Toggle on={theme === "light"} onChange={toggleTheme} label="Light theme" />
          </Row>
        </Section>

        <Section
          title="Security"
          aside={
            <SecurityScore
              checks={[user?.email_verified, user?.mobile_verified, user?.totp_enabled, Boolean(phishCode), user?.kyc_status === "approved", user?.safe_session]}
            />
          }
        >
          <Row label="Two-factor authentication" hint="Ask for a code from your authenticator on every login." testid="settings-card-2fa">
            <Toggle on={user?.totp_enabled} onChange={() => setDrawer("2fa")} label="Two-factor authentication" />
          </Row>
          <Row label="Mobile number" hint={user?.mobile_verified ? "Verified" : "Saved at signup. Verify it here when you are ready."}>
            {user?.mobile_verified ? (
              <span className="num text-sm text-muted-foreground">{user?.mobile_number || "—"}</span>
            ) : (
              <button type="button" onClick={() => setVerify("mobile")} className={smallBtn}>
                Verify via OTP
              </button>
            )}
          </Row>
          <Row label="Email ID" hint={user?.email}>
            {user?.email_verified ? (
              <span className="text-xs text-up">Verified</span>
            ) : (
              <button type="button" onClick={() => setVerify("email")} className={smallBtn}>
                Verify via OTP
              </button>
            )}
          </Row>
          <Row label="Anti-phishing code" hint={phishCode ? `“${phishCode}” appears in every official email.` : "Add a personal code shown in every email we send."}>
            <button type="button" onClick={() => setDrawer("anti_phishing")} className={smallBtn} data-testid="settings-card-anti-phishing">
              {phishCode ? "Edit" : "Set code"}
            </button>
          </Row>
          <Row label="Safe session" hint="Auto-logout after 10 minutes of inactivity." testid="settings-card-safe-session">
            <Toggle on={user?.safe_session} onChange={toggleSafeSession} disabled={safeBusy} label="Safe session" />
          </Row>
          <Row label="Trusted access" hint={user?.can_trade ? "Trading unlocked" : "Unlock trading via KYC, wallet, IP or device trust."}>
            <button type="button" onClick={() => setDrawer("trust")} className={smallBtn} data-testid="settings-card-trust">
              Manage
            </button>
          </Row>
          <Row label="Active sessions" hint="Devices currently signed in to your account.">
            <Link to="/profile/sessions" className={smallBtn} data-testid="settings-card-activity">
              View
            </Link>
          </Row>
        </Section>

        <Section title="Notifications">
          {NOTIF_ROWS.map(([k, label]) => (
            <Row key={k} label={label}>
              <Toggle on={prefs?.[k]} onChange={(v) => savePref(k, v)} disabled={!prefs} label={label} />
            </Row>
          ))}
        </Section>

        <PaymentMethods />

        <section className="rounded-xl border border-down/30 bg-down/5 p-6" data-testid="settings-card-delete">
          <h2 className="text-sm font-semibold text-down">Danger zone</h2>
          <p className="mt-2 text-xs text-muted-foreground">
            {pendingDeletion
              ? "Your account is scheduled for deletion. You can cancel this before the cooling-off period ends."
              : "Deleting closes open orders, locks withdrawals and permanently removes your account after a cooling-off period."}
          </p>
          <button type="button" onClick={() => setDrawer("delete")} className="mt-4 rounded-lg border border-down/40 px-3 py-2 text-xs text-down transition hover:bg-down/10">
            {pendingDeletion ? "Cancel deletion" : "Delete account"}
          </button>
        </section>
      </div>

      {verify && (
        <VerifyContactModal
          type={verify}
          savedPhone={verify === "mobile" ? user?.mobile_number || "" : ""}
          onClose={() => setVerify(null)}
          onVerified={fetchMe}
        />
      )}
      {drawer === "2fa" && (
        <Drawer title="Two-factor authentication" onClose={closeDrawer} width="max-w-md">
          <TwoFactorPanel user={user} onChanged={fetchMe} />
        </Drawer>
      )}
      {drawer === "anti_phishing" && (
        <Drawer title="Anti-phishing code" onClose={closeDrawer} width="max-w-md">
          <AntiPhishingPanel
            current={phishCode}
            onSaved={(v) => {
              setPhishCode(v);
              setDrawer(null);
            }}
          />
        </Drawer>
      )}
      {drawer === "delete" && (
        <Drawer title="Delete account" onClose={closeDrawer} width="max-w-md">
          <DeleteAccountPanel user={user} onChanged={fetchMe} onClose={closeDrawer} />
        </Drawer>
      )}
      {drawer === "trust" && (
        <Drawer title="Trusted access" onClose={closeDrawer}>
          <TrustCenter onClose={closeDrawer} />
        </Drawer>
      )}
    </AppShell>
  );
}
